import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BrowserView, MobileView } from "react-device-detect";
import { ThemeProvider } from "@mui/material/styles";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { AuthenticationResult } from "@azure/msal-browser";

import { PlantaoVendasProvider } from "contexts/PlantaoVendasContext";
import { Context } from "./context/Context";
import theme from "./theme";
import { AppRoutes } from "./routes/index";
import MobileRedirect from "./screens/mobileRedirect";

// Imports MSAL e Redux
import { msalInstance, config } from "./utils/auth_azure";
import { signInRequest } from "./store/ducks/auth/actions";
import useTokenHealth from "./hooks/useTokenHealth";

// Ajuste o tipo conforme seu store
import { UserState } from "./interfaces";

interface FiltersReportsInterface {
  PalavrasChave: string;
  PaginaAtual: number;
  TotalItensRetorno: number;
}

// Wrapper que usa useLocation para reagir à navegação (evita MobileView na rota /deeplink)
function AppView({ content }: { content: React.ReactNode }): JSX.Element {
  const { pathname, hash = "" } = useLocation();
  const windowPath =
    typeof window !== "undefined" ? window.location.pathname : "";
  const windowHash = typeof window !== "undefined" ? window.location.hash : "";
  const isDeepLink =
    pathname.includes("/deeplink") ||
    hash.includes("/deeplink") ||
    windowPath.includes("/deeplink") ||
    windowHash.includes("/deeplink");
  const isTermosDeUso =
    pathname.includes("/termos-de-uso") || hash.includes("/termos-de-uso");
  const skipMobileRedirect = isTermosDeUso;

  if (isDeepLink) {
    return <>{content}</>;
  }
  return (
    <>
      <BrowserView>{content}</BrowserView>
      <MobileView>
        {skipMobileRedirect ? <AppRoutes /> : <MobileRedirect />}
      </MobileView>
    </>
  );
}

export default function App(): JSX.Element {
  // ===============================
  // 1) Estados de filtros
  // ===============================
  const [filters, setFilters] = useState<any>(getInitialLeadsFiltersState);
  const [filtersPDV, setFiltersPDV] = useState<any>(getInitialPDVFiltersState);
  const [filtersReports, setFiltersReports] = useState<any>(
    getInitialReportsFiltersState
  );
  const [productsFilters, setProductsFilters] =
    useState<FiltersReportsInterface>(getInitialProductsFiltersState);

  // ===============================
  // 2) Estados e actions do Redux
  // ===============================
  const dispatch = useDispatch();
  const { signed } = useSelector<RootStateOrAny, UserState>(
    (state) => state.user
  );

  // ===============================
  // 3) Hook para verificação de token
  // ===============================
  useTokenHealth(); // Usa nosso hook personalizado para gerenciar o token

  // ===============================
  // 4) useEffect para Silent Login (MSAL)
  // ===============================
  useEffect(() => {
    // Função para autenticação silenciosa
    const attemptSilentLogin = async () => {
      if (!signed) {
        try {
          console.log("[App] Tentando login silencioso");
          const accounts = msalInstance.getAllAccounts();

          // Verifica se há conta no cache MSAL
          if (accounts && accounts.length > 0) {
            console.log(
              "[App] Conta encontrada no cache, adquirindo token silenciosamente"
            );

            const silentRequest = {
              scopes: config.scopes,
              account: accounts[0],
              forceRefresh: false,
            };

            try {
              const response = await msalInstance.acquireTokenSilent(
                silentRequest
              );
              console.log("[App] Token adquirido com sucesso");

              // Dispara action para login com o token adquirido
              dispatch(
                signInRequest(response.accessToken, response.account?.username)
              );
            } catch (silentError) {
              console.error("[App] Erro no login silencioso:", silentError);
              // Não tenta login popup automaticamente, deixa usuário clicar no botão
            }
          } else {
            console.log("[App] Nenhuma conta encontrada no cache MSAL");
          }
        } catch (error) {
          console.error("[App] Erro na verificação de contas:", error);
        }
      }
    };

    // Tenta login silencioso na inicialização
    attemptSilentLogin();

    // Não adicionar signed como dependência para evitar loops
  }, [dispatch]);

  // ===============================
  // 5) Lógica de persistência (filtros)
  // Salva apenas quando o usuário está logado
  // ===============================
  useEffect(() => {
    if (!signed) return; // Não salvar se não estiver logado
    localStorage.setItem(
      "@sell.it/reportsLeadFilters",
      JSON.stringify(filtersReports)
    );
  }, [filtersReports, signed]);

  useEffect(() => {
    if (!signed) return; // Não salvar se não estiver logado
    localStorage.setItem("@sell.it/leadFilters", JSON.stringify(filters));
  }, [filters, signed]);

  useEffect(() => {
    if (!signed) return; // Não salvar se não estiver logado
    localStorage.setItem("@sell.it/PDVFilters", JSON.stringify(filtersPDV));
  }, [filtersPDV, signed]);

  useEffect(() => {
    if (!signed) return; // Não salvar se não estiver logado
    localStorage.setItem(
      "@sell.it/productsFilters",
      JSON.stringify(productsFilters)
    );
  }, [productsFilters, signed]);

  // ===============================
  // 6) useMemo para compartilhar no Context
  // ===============================
  const value = useMemo(() => {
    return {
      filters,
      setFilters,
      productsFilters,
      setProductsFilters,
      filtersReports,
      setFiltersReports,
      filtersPDV,
      setFiltersPDV,
    };
  }, [filters, productsFilters, filtersReports, filtersPDV]);

  // ===============================
  // 7) Renderização
  // BrowserRouter no topo para useLocation reagir à navegação; na rota /deeplink não usa MobileView
  // ===============================
  const content = (
    <PlantaoVendasProvider>
      <AppRoutes />
    </PlantaoVendasProvider>
  );

  return (
    <ThemeProvider theme={theme}>
      <Context.Provider value={value}>
        <AppView content={content} />
      </Context.Provider>
    </ThemeProvider>
  );
}

// ===========================================
// 8) Funções de inicialização dos filtros
// ===========================================
function getInitialPDVFiltersState() {
  const filtersPDVValue = localStorage.getItem("@sell.it/PDVFilters");
  return filtersPDVValue
    ? JSON.parse(filtersPDVValue)
    : {
        Visitante: null,
        Corretor: null,
        TipoDeVisita: null,
        EmpresaDeVendas: null,
        Equipe: null,
        Periodo: {
          PeriodoDe: null,
          PeriodoAte: null,
        },
        paginationParams: {
          PaginaAtual: 1,
          PalavrasChave: "",
          TotalItensRetorno: 100,
          OrdenarPorOrdemAlfabetica: "",
        },
      };
}

function getInitialLeadsFiltersState() {
  const filtersValue = localStorage.getItem("@sell.it/leadFilters");
  return filtersValue
    ? JSON.parse(filtersValue)
    : {
        OrdenarPorRecentes: null,
        IdentificaCliente: null,
        InicioIntervaloCadastroLead: null,
        FimIntervaloCadastroLead: null,
        InicioIntervaloTransferencia: null,
        FimIntervaloTransferencia: null,
        FimIntervaloValorImovel: "",
        InicioIntervaloValorImovel: "",
        FiltrarRecebidosPorTransferencia: null,
        IdsCanalContato: [],
        paginationParams: {
          PaginaAtual: 1,
          PalavrasChave: "",
          TotalItensRetorno: 100,
          OrdenarPorOrdemAlfabetica: "",
        },
        OrdenacaoLeads: null,
        OrdenacaoCrescente: undefined,
        TipoVisualizacao: 1,
        order1: null,
        order2: null,
        order3: null,
        order4: null,
        order5: null,
        order6: null,
        order7: null,
        order8: null,
        order13: null,
      };
}

function getInitialReportsFiltersState() {
  const filtersValue = localStorage.getItem("@sell.it/reportsLeadFilters");
  return filtersValue
    ? JSON.parse(filtersValue)
    : {
        PaginaAtual: 1,
        PalavrasChave: "",
        TotalItensRetorno: 5,
      };
}

function getInitialProductsFiltersState() {
  const filtersValue = localStorage.getItem("@sell.it/productsFilters");
  return filtersValue
    ? JSON.parse(filtersValue)
    : {
        OrdenarPorRecentes: null,
        InicioIntervaloMetragemImovel: null,
        FimIntervaloMetragemImovel: null,
      };
}
